import {
  DecarbonizationPillar,
  Scope,
  Solution,
  SolutionOption,
  CostScore,
  SolutionOptionKey,
  SolutionMap,
} from "@labs/shared/types/Solution.ts";
import groupBy from "ramda/es/groupBy";
import prop from "ramda/es/prop";
import { FuelSource } from "@labs/shared/types/FuelSource.ts";

export const solutionOptions: SolutionOption[] = [
  {
    name: SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryElectricity,
    label: "Energy Efficiency - Electricity",
    pillar: DecarbonizationPillar.EnergyEfficiency,
    targetedFuelSource: FuelSource.Electricity,
    description:
      "Use the slider to mark your planned percentage decrease of electric energy intensity. Energy intensity is the quantity of energy required per unit output or activity, so using less energy to produce a product reduces the intensity. Cascade Energy recommendation: 25%.",
    scope: Scope.Scope2,
    costScore: CostScore.One,
  },
  {
    name: SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryNaturalGas,
    label: "Energy Efficiency - Natural Gas",
    pillar: DecarbonizationPillar.EnergyEfficiency,
    targetedFuelSource: FuelSource.NaturalGas,
    description:
      "Use the slider to mark your planned percentage decrease of natural gas energy intensity. Energy intensity is the quantity of energy required per unit of output or activity. Invest in high-efficiency equipment, controls, operations, and maintenance to help achieve reduction goals. Cascade Energy recommendation: 15%.",
    scope: Scope.Scope1,
    costScore: CostScore.One,
  },
  {
    name: SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryPropane,
    label: "Energy Efficiency - Propane",
    pillar: DecarbonizationPillar.EnergyEfficiency,
    targetedFuelSource: FuelSource.Propane,
    description:
      "Use the slider to mark your planned percentage decrease of propane energy intensity. Energy intensity is the quantity of energy required per unit of output or activity. Optimizing your propane combustion and transitioning to high-efficiency propane-powered forklifts and heaters can help get you to your goal. Cascade Energy recommendation: 15%.",
    scope: Scope.Scope1,
    costScore: CostScore.One,
  },
  {
    name: SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryFuelOil,
    label: "Energy Efficiency - Fuel Oil",
    pillar: DecarbonizationPillar.EnergyEfficiency,
    targetedFuelSource: FuelSource.FuelOil,
    description:
      "Use the slider to mark your planned percentage decrease of fuel oil energy intensity. Energy intensity is the quantity of energy required per unit of output or activity. Installing heat recovery systems and improving your building envelope can help reduce your fuel oil expenses. Cascade Energy recommendation: 15%.",
    scope: Scope.Scope1,
    costScore: CostScore.One,
  },
  {
    name: SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryDiesel,
    label: "Energy Efficiency - Diesel",
    pillar: DecarbonizationPillar.EnergyEfficiency,
    targetedFuelSource: FuelSource.Diesel,
    description:
      "Use the slider to mark your planned percentage decrease of diesel energy intensity. Energy intensity is the quantity of energy required per unit of output or activity. Introduce high-efficiency equipment, idle reduction policies and/or route optimization to help achieve reduction goals. Cascade Energy recommendation: 15%.",
    scope: Scope.Scope1,
    costScore: CostScore.One,
  },
  {
    name: SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryGasoline,
    label: "Energy Efficiency - Gasoline",
    pillar: DecarbonizationPillar.EnergyEfficiency,
    targetedFuelSource: FuelSource.Gasoline,
    description:
      "Use the slider to mark your planned percentage decrease of gasoline energy intensity. Energy intensity is the quantity of energy required per unit of output or activity. Keep up to date maintenance on existing fleet and gasoline powered equipment. Introduce high-efficiency equipment, idle reduction policies and/or route optimization to help achieve reduction goals. Cascade Energy recommendation: 15%.",
    scope: Scope.Scope1,
    costScore: CostScore.One,
  },
  {
    name: SolutionOptionKey.StationaryElectrificationNaturalGas,
    label: "Stationary Electrification - Natural Gas",
    pillar: DecarbonizationPillar.Electrification,
    targetedFuelSource: FuelSource.NaturalGas,
    description:
      "Use the slider to mark your planned percentage of natural gas reduction by electrifying equipment and systems. Replace legacy gas-powered equipment with heat pumps or electric furnaces and boilers.",
    scope: Scope.Scope1,
    costScore: CostScore.Three,
  },
  {
    name: SolutionOptionKey.StationaryElectrificationPropane,
    label: "Stationary Electrification - Propane",
    pillar: DecarbonizationPillar.Electrification,
    targetedFuelSource: FuelSource.Propane,
    description:
      "Use the slider to mark your planned percentage of propane reduction by electrifying equipment and systems. Replace old propane-fueled heaters, forklifts, or other equipment with electric or hybrid options.",
    scope: Scope.Scope1,
    costScore: CostScore.Two,
  },
  {
    name: SolutionOptionKey.StationaryElectrificationFuelOil,
    label: "Stationary Electrification - Fuel Oil",
    pillar: DecarbonizationPillar.Electrification,
    targetedFuelSource: FuelSource.FuelOil,
    description:
      "Use the slider to mark your planned percentage of fuel oil reduction by electrifying equipment and systems. Replace fuel oil boilers with electric heat pumps or furnaces.",
    scope: Scope.Scope1,
    costScore: CostScore.Two,
  },
  {
    name: SolutionOptionKey.MobileElectrificationDiesel,
    label: "Mobile Electrification - Diesel",
    pillar: DecarbonizationPillar.Electrification,
    targetedFuelSource: FuelSource.Diesel,
    description:
      "Use the slider to mark your planned percentage of diesel reduction by electrifying equipment and systems. Replace diesel-powered trucks, forklifts, or equipment with electric or hybrid alternatives.",
    scope: Scope.Scope1,
    costScore: CostScore.Two,
  },
  {
    name: SolutionOptionKey.MobileElectrificationGasoline,
    label: "Mobile Electrification - Gasoline",
    pillar: DecarbonizationPillar.Electrification,
    targetedFuelSource: FuelSource.Gasoline,
    description:
      "Use the slider to mark your planned percentage of gasoline reduction by electrifying equipment and systems. Replace gasoline-powered vehicles with electric or hybrid alternatives.",
    scope: Scope.Scope1,
    costScore: CostScore.Two,
  },
  {
    name: SolutionOptionKey.LowNoCarbonFuelSwitchingNaturalGas,
    label: "Low/No Carbon Fuel Switching - Natural Gas",
    pillar: DecarbonizationPillar.LowCarbonFuels,
    targetedFuelSource: FuelSource.NaturalGas,
    description:
      "Use the slider to mark your planned reduction in carbon intensity. Low- or zero-carbon fuels are not widely available at a competitive price point. Typically this solution will not play a major role in short-term decarbonization plans, but may be important in the coming decades.",
    scope: Scope.Scope1,
    costScore: CostScore.Four,
  },
  {
    name: SolutionOptionKey.LowNoCarbonFuelSwitchingPropane,
    label: "Low/No Carbon Fuel Switching - Propane",
    pillar: DecarbonizationPillar.LowCarbonFuels,
    targetedFuelSource: FuelSource.Propane,
    description:
      "Use the slider to mark your planned reduction in carbon intensity. Low- or zero-carbon fuels are not widely available at a competitive price point. Typically this solution will not play a major role in short-term decarbonization plans, but may be important in the coming decades.",
    scope: Scope.Scope1,
    costScore: CostScore.Four,
  },
  {
    name: SolutionOptionKey.LowNoCarbonFuelSwitchingFuelOil,
    label: "Low/No Carbon Fuel Switching - Fuel Oil",
    pillar: DecarbonizationPillar.LowCarbonFuels,
    targetedFuelSource: FuelSource.FuelOil,
    description:
      "Use the slider to mark your planned reduction in carbon intensity. Low- or zero-carbon fuels are not widely available at a competitive price point. Typically this solution will not play a major role in short-term decarbonization plans, but may be important in the coming decades.",
    scope: Scope.Scope1,
    costScore: CostScore.Four,
  },
  {
    name: SolutionOptionKey.LowNoCarbonFuelSwitchingGasoline,
    label: "Low/No Carbon Fuel Switching - Gasoline",
    pillar: DecarbonizationPillar.LowCarbonFuels,
    targetedFuelSource: FuelSource.Gasoline,
    description:
      "Use the slider to mark your planned reduction in carbon intensity. Low- or zero-carbon fuels are not widely available at a competitive price point. Typically this solution will not play a major role in short-term decarbonization plans, but may be important in the coming decades.",
    scope: Scope.Scope1,
    costScore: CostScore.Four,
  },
  {
    name: SolutionOptionKey.LowNoCarbonFuelSwitchingDiesel,
    label: "Low/No Carbon Fuel Switching - Diesel",
    pillar: DecarbonizationPillar.LowCarbonFuels,
    targetedFuelSource: FuelSource.Diesel,
    description:
      "Use the slider to mark your planned reduction in carbon intensity. Low- or zero-carbon fuels are not widely available at a competitive price point. Typically this solution will not play a major role in short-term decarbonization plans, but may be important in the coming decades.",
    scope: Scope.Scope1,
    costScore: CostScore.Two,
  },
  {
    name: SolutionOptionKey.CoGenerationElectricity,
    label: "Co-Generation - Electricity",
    pillar: DecarbonizationPillar.LowCarbonFuels,
    targetedFuelSource: FuelSource.Electricity,
    description:
      "Use the slider to plan reductions in electricity purchased from grid. Onsite co-generation, typically with natural gas, can be a short-term decarbonization solution because it reduces electricity purchased from the grid while often resulting in a minimal increase in overall gas use. However, it may not be a compatible solution with a longer-term net-zero target.",
    scope: Scope.Scope2,
    costScore: CostScore.Four,
  },
  {
    name: SolutionOptionKey.RenewablePurchaseFromUtilityElectricity,
    label: "Renewable Purchase from Utility - Electricity",
    pillar: DecarbonizationPillar.LowCarbonFuels,
    targetedFuelSource: FuelSource.Electricity,
    description:
      "Use the slider to plan the amount of emissions-free electricity purchases. Some utilities offer optional premiums to purchase emissions-free electricity. Cascade Energy recommendation: Consider this option where available.",
    scope: Scope.Scope2,
    costScore: CostScore.Three,
  },
  {
    name: SolutionOptionKey.RenewableEnergyAsPpaVppaElectricity,
    label: "Renewable Energy as PPA/VPPA - Electricity",
    pillar: DecarbonizationPillar.LowCarbonFuels,
    targetedFuelSource: FuelSource.Electricity,
    description:
      "Use the slider to plan the amount of emissions-free electricity purchases. In some parts of the country, you can choose where you buy your power. Electricity sellers offer power purchase agreements (PPAs) to buy clean electricity. Cascade Energy recommendation: Consider this option where available.",
    scope: Scope.Scope2,
    costScore: CostScore.Three,
  },
  {
    name: SolutionOptionKey.RenewableCreditsRecsElectricity,
    label: "Renewable Energy Credits (RECs) - Electricity",
    pillar: DecarbonizationPillar.LowCarbonFuels,
    targetedFuelSource: FuelSource.Electricity,
    description:
      "Use the slider to mark your planned amount of REC purchases to offset electric emissions. Buying renewable energy credits (RECs) contributes to the generation of renewable energy even if local policy doesn't let you buy that energy directly from your utility or on the competitive market. Cascade Energy recommendation: Consider this option where available.",
    scope: Scope.Scope2,
    costScore: CostScore.Three,
  },
  {
    name: SolutionOptionKey.OnsiteSolarOrRenewableEnergyGenerationElectricity,
    label: "Onsite solar or renewable energy generation - Electricity",
    pillar: DecarbonizationPillar.LowCarbonFuels,
    targetedFuelSource: FuelSource.Electricity,
    description:
      "Use the slider to mark your planned generation coming from emission-free onsite sources. Generating clean power onsite, typically through solar, can be highly cost-effective and can be tailored with numerous financing and ownership models. Cascade Energy recommendation: Consider this option where feasible.",
    scope: Scope.Scope2,
    costScore: CostScore.Two,
  },
  {
    name: SolutionOptionKey.CarbonCaptureAndSequestrationCarbon,
    label: "Carbon capture, Utilization and Storage",
    pillar: DecarbonizationPillar.CarbonCapture,
    targetedFuelSource: FuelSource.Carbon,
    description:
      "Use the slider to plan reductions in remaining carbon footprint. Carbon capture, utilization, and storage refers to technology that captures CO2 emissions and either reuses or stores that CO2 so it will not enter the atmosphere. Cascade Energy recommendation: This technology is likely decades away from cost-effectiveness, so we don't recommend applying it to short-term emissions reduction plans.",
    scope: Scope.Scope1,
    costScore: CostScore.Five,
  },
  {
    name: SolutionOptionKey.EliminateFugitiveEmissionsCarbon,
    label: "Eliminate Fugitive Emissions",
    pillar: DecarbonizationPillar.CarbonCapture,
    targetedFuelSource: FuelSource.Carbon,
    description:
      "Use the slider to plan reductions in remaining carbon footprint. Fugitive emissions are unintentional releases of greenhouse gasses into the atmosphere. They are uncommon in most industries. Cascade Energy recommendation: Monitor and eliminate fugitive emissions where possible.",
    scope: Scope.Scope1,
    costScore: CostScore.Four,
  },
  {
    name: SolutionOptionKey.OffsetsCarbon,
    label: "Offsets",
    pillar: DecarbonizationPillar.CarbonCapture,
    targetedFuelSource: FuelSource.Carbon,
    description:
      "Use the slider to plan reductions in remaining carbon footprint. Carbon offsets, also known as environmental attribute certificates, are a trading mechanism to fund decarbonization projects outside an organization's own emissions footprint. Offsets can have appealing benefits because organizations may find it cheaper in the short-term to buy offsets rather than reduce their own footprint. However, offsets are not allowed as a scope 1 or scope 2 reduction mechanism in the Science Based Target Initiative.",
    scope: Scope.Scope1,
    costScore: CostScore.Three,
  },
  {
    name: SolutionOptionKey.RefrigerantLeaksRefrigerants,
    label: "Refrigerant Leaks",
    pillar: DecarbonizationPillar.CarbonCapture,
    targetedFuelSource: FuelSource.Refrigerants,
    description:
      "Use the slider to plan reductions in refrigerant leaks. Some refrigerant gases have high global warming potential (GWP) when released into the atmosphere. There are two primary ways to reduce emissions from refrigerants: reduce leaks and replace refrigerants with lower GWP alternatives.",
    scope: Scope.Scope1,
    costScore: CostScore.One,
  },
  {
    name: SolutionOptionKey.LowGwpRefrigerantsRefrigerants,
    label: "Low-GWP Refrigerants",
    pillar: DecarbonizationPillar.CarbonCapture,
    targetedFuelSource: FuelSource.Refrigerants,
    description:
      "Use the slider to plan reductions in refrigerant GWP. Some refrigerant gases have high global warming potential (GWP) when released into the atmosphere. There are two primary ways to reduce emissions from refrigerants: reduce leaks and replace refrigerants with lower GWP alternatives. Refrigerant replacements have varying GWP factors. You’ll need to know the relative GWP of the existing and the proposed replacement(s), then input that ratio here.",
    scope: Scope.Scope1,
    costScore: CostScore.Two,
  },
];

export const solutionOptionsWithValue: Solution[] = solutionOptions.map(
  (option) => ({
    ...option,
    label: option.label,
    value: 0,
  }),
);

export const solutionOptionsByName: Record<string, Solution> =
  solutionOptionsWithValue.reduce(
    (acc, option) => {
      acc[option.name] = option;
      return acc;
    },
    {} as Record<string, Solution>,
  );

export const solutionMap: SolutionMap = solutionOptionsWithValue.reduce(
  (acc, option) => {
    acc[option.name] = option.value;
    return acc;
  },
  {} as Record<string, number>,
);
export const solutionsByPillar: Record<string, Solution[]> = groupBy(
  prop("pillar"),
  solutionOptionsWithValue,
);
